import { useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useAuth } from '@/contexts/AuthContext'
import {
    LayoutDashboard,
    Monitor,
    Package,
    Users,
    Truck,
    FileText,
    LogOut,
    Store,
    ChevronLeft,
    ChevronRight,
    RotateCcw,
    RotateCw,
    Receipt,
    Landmark,
    BookOpen,
    CalendarDays,
    Scale,
    Menu,
    X,
    FilePlus,
    ClipboardList,
    Building2,
} from 'lucide-react'

const NAV_SECTIONS = [
    {
        title: 'MAIN',
        items: [
            { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { path: '/admin/counter', label: 'Point of Sale', icon: Monitor },
        ]
    },
    {
        title: 'MANAGEMENT',
        items: [
            { path: '/admin/menu', label: 'Products', icon: Package },
            { path: '/admin/staff', label: 'Customers', icon: Users },
            { path: '/admin/settings', label: 'Suppliers', icon: Truck },
        ]
    },
    {
        title: 'TRANSACTIONS',
        items: [
            { path: '/admin/quotations', label: 'Quotations', icon: ClipboardList },
            { path: '/admin/sales-entry', label: 'Sales Entry', icon: FilePlus },
            { path: '/admin/purchase-invoices', label: 'Purchase Entry', icon: FilePlus },
            { path: '/admin/sales-returns', label: 'Sales Return', icon: RotateCcw },
            { path: '/admin/purchase-returns', label: 'Purchase Return', icon: RotateCw },
            { path: '/admin/vouchers', label: 'Vouchers', icon: Receipt },
        ]
    },
    {
        title: 'ACCOUNTING',
        items: [
            { path: '/admin/accounts', label: 'Chart of Accounts', icon: Landmark },
            { path: '/admin/ledger', label: 'Account Ledger', icon: BookOpen },
            { path: '/admin/daybook', label: 'Day Book', icon: CalendarDays },
            { path: '/admin/trial-balance', label: 'Trial Balance', icon: Scale },
        ]
    },
    {
        title: 'COMPLIANCE',
        items: [
            { path: '/admin/reports', label: 'FBR Invoices', icon: FileText },
        ]
    },
    {
        title: 'SETTINGS',
        items: [
            { path: '/admin/company', label: 'Company Profile', icon: Building2 },
        ]
    },
]

export function AppSidebar() {
    const [collapsed, setCollapsed] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const location = useLocation()
    const { user, logout } = useAuth()

    const initials = user?.displayName
        ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : 'A'

    const sidebarContent = (
        <>
            {/* Brand */}
            <div className={`flex items-center ${collapsed && !mobileOpen ? 'justify-center px-2' : 'px-5'} py-6 border-b border-blue-100`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', boxShadow: '0 4px 15px rgba(59,130,246,0.3)' }}>
                    <Store className="w-5 h-5 text-white" />
                </div>
                {(!collapsed || mobileOpen) && (
                    <div className="ml-3 min-w-0 overflow-hidden">
                        <div className="text-[15px] font-bold text-slate-800 tracking-tight leading-none">HardwarePOS</div>
                        <div className="text-[10px] text-blue-500/70 mt-1.5 font-semibold tracking-[0.2em] uppercase">Cloud Edition</div>
                    </div>
                )}
                {/* Close button for mobile */}
                {mobileOpen && (
                    <button onClick={() => setMobileOpen(false)} className="ml-auto p-1 text-slate-400 hover:text-slate-600 lg:hidden">
                        <X className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* Nav Sections */}
            <nav className="flex-1 mt-2 px-3 overflow-y-auto">
                {NAV_SECTIONS.map((section, sIdx) => (
                    <div key={section.title} className={sIdx > 0 ? 'mt-5' : 'mt-1'}>
                        {(!collapsed || mobileOpen) ? (
                            <div className="px-3 mb-2">
                                <span className="text-[10px] font-bold text-blue-400 tracking-[0.2em]">{section.title}</span>
                            </div>
                        ) : (
                            sIdx > 0 && <div className="mx-2 my-3 border-t border-blue-200/50" />
                        )}

                        <div className="space-y-1">
                            {section.items.map((item) => {
                                const active = location.pathname === item.path
                                const Icon = item.icon
                                return (
                                    <Link
                                        key={item.label}
                                        to={item.path}
                                        onClick={() => setMobileOpen(false)}
                                        className={`group relative flex items-center ${collapsed && !mobileOpen ? 'justify-center' : ''} gap-3 ${collapsed && !mobileOpen ? 'px-2' : 'px-3'} py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200
                                            ${active
                                                ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20'
                                                : 'text-slate-600 hover:text-blue-700 hover:bg-blue-50'
                                            }`}
                                        title={collapsed && !mobileOpen ? item.label : undefined}
                                    >
                                        <Icon className={`w-5 h-5 flex-shrink-0 transition-all duration-200 ${active ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'}`} />
                                        {(!collapsed || mobileOpen) && <span className="truncate">{item.label}</span>}
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Collapse Toggle - desktop only */}
            <div className="px-3 py-2 border-t border-blue-100 hidden lg:block">
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-2.5 px-3 py-2 rounded-xl text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition-all duration-200 text-[12px] font-medium`}
                >
                    {collapsed
                        ? <ChevronRight className="w-4 h-4" />
                        : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>
                    }
                </button>
            </div>

            {/* User Footer */}
            <div className="px-3 py-4 border-t border-blue-100">
                <div className={`flex items-center ${collapsed && !mobileOpen ? 'justify-center' : ''} gap-3`}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', boxShadow: '0 2px 10px rgba(59,130,246,0.25)' }}>
                        {initials}
                    </div>
                    {(!collapsed || mobileOpen) && (
                        <>
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-semibold text-slate-700 truncate">
                                    {user?.displayName || 'User'}
                                </div>
                                <div className="text-[10px] text-slate-400 truncate">{user?.email || 'admin'}</div>
                            </div>
                            <button
                                onClick={logout}
                                className="text-slate-400 hover:text-red-500 transition-all duration-200 p-2 rounded-xl hover:bg-red-50"
                                title="Logout"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    )

    return (
        <>
            {/* Mobile Hamburger Button */}
            <button
                onClick={() => setMobileOpen(true)}
                className="fixed top-3 left-3 z-50 lg:hidden bg-white p-2 rounded-xl shadow-lg border border-slate-200 hover:bg-slate-50 transition-all"
            >
                <Menu className="w-5 h-5 text-slate-700" />
            </button>

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
            )}

            {/* Mobile Drawer */}
            <aside
                className={`fixed top-0 left-0 z-50 h-full w-[270px] flex flex-col transition-transform duration-300 lg:hidden
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
                style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #e8f1fb 100%)' }}
            >
                {sidebarContent}
            </aside>

            {/* Desktop Sidebar */}
            <aside
                className="hidden lg:flex flex-col h-screen transition-all duration-300 relative select-none overflow-hidden border-r border-blue-100"
                style={{
                    width: collapsed ? '72px' : '250px',
                    background: 'linear-gradient(180deg, #f0f7ff 0%, #e8f1fb 100%)',
                }}
            >
                {sidebarContent}
            </aside>
        </>
    )
}
